import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import { ChevronDown, ChevronRight, FileEarmarkArrowUp, Plus, Send, Upload } from 'react-bootstrap-icons';
import {
  arcanumAgentsLibraryService,
  parseExportedAgent,
  type LibraryAgent,
  type LibraryReferenceFile,
} from '@/services/arcanumAgentsLibraryService';
import { agentDeployService, type PreviewResult } from '@/services/agentDeployService';
import { clientService } from '@/services/clientService';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, reasonOf } from '@/components/tools/clientSelectionUtils';
import { ToolPage } from '@/components/ui/ToolPage';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { Client } from '@/types';
import './AgentLibrary.css';

const AGENT_TYPES: { value: string; label: string }[] = [
  { value: 'task', label: 'Task' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'scheduled', label: 'Scheduled' },
];

type DeployState = 'queued' | 'running' | 'success' | 'partial' | 'failed';

const DEPLOY_BADGE: Record<DeployState, { bg: string; label: string }> = {
  queued: { bg: 'secondary', label: 'Waiting' },
  running: { bg: 'info', label: 'Sending' },
  success: { bg: 'success', label: 'Done' },
  partial: { bg: 'warning', label: 'Partly done' },
  failed: { bg: 'danger', label: 'Failed' },
};

const emptyDraft = (): Partial<LibraryAgent> => ({
  title: '',
  description: '',
  system_prompt: '',
  user_instructions: '',
  agent_type: 'task',
  icon: '',
  tags: [],
  tools_config: { queryDataSources: true },
  reference_files: [],
});

function newDraftId(): string {
  const uuid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  return `lib_${uuid.replace(/-/g, '')}`;
}

export default function AgentLibrary() {
  const [agents, setAgents] = useState<LibraryAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAgents(await arcanumAgentsLibraryService.listAgents());
    } catch (e) {
      setError(`The library did not load. ${reasonOf(e)} Reload the page to try again.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  return (
    <ToolPage
      title="Agent library"
      subtitle="Keep Arcanum's own agents and send them to client workspaces without a deploy."
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs defaultActiveKey="library" className="agents-tabs">
        <Tab
          eventKey="library"
          title={
            <>
              Library
              {!loading && <span className="agents-tab-count">{agents.length}</span>}
            </>
          }
        >
          <LibraryTab agents={agents} loading={loading} onChanged={loadAgents} onError={setError} />
        </Tab>
        <Tab eventKey="deploy" title="Send to clients">
          <DeployTab agents={agents} onError={setError} />
        </Tab>
      </Tabs>
    </ToolPage>
  );
}

// ─── Library tab ──────────────────────────────────────────────────────────────
function LibraryTab({
  agents,
  loading,
  onChanged,
  onError,
}: {
  agents: LibraryAgent[];
  loading: boolean;
  onChanged: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState<Partial<LibraryAgent> | null>(null);
  const [deleting, setDeleting] = useState<LibraryAgent | null>(null);
  const [busy, setBusy] = useState(false);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await arcanumAgentsLibraryService.deleteAgent(deleting);
      setDeleting(null);
      await onChanged();
    } catch (e) {
      setDeleting(null);
      onError(`The agent was not deleted. ${reasonOf(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const { draft, warnings } = parseExportedAgent(text);
      setImportWarnings(warnings);
      setEditing(draft);
    } catch (err) {
      onError(`That file could not be read as an agent. ${reasonOf(err)} Use the file Numa makes with Export.`);
    }
  };

  return (
    <>
      <div className="agents-toolbar">
        <p className="agents-toolbar__note">
          Add an agent from the file Numa makes with <strong>Export</strong> on the agent, then attach its reference
          files.
        </p>
        <div className="agents-toolbar__actions">
          <input ref={fileInputRef} type="file" accept="application/json,.json" hidden onChange={handleUploadFile} />
          <Button
            variant="outline-secondary"
            onClick={() => {
              setImportWarnings([]);
              setEditing({ ...emptyDraft(), library_agent_id: newDraftId() });
            }}
          >
            <Plus className="me-1" aria-hidden="true" />
            New blank agent
          </Button>
          <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
            <FileEarmarkArrowUp className="me-1" aria-hidden="true" />
            Upload an agent
          </Button>
        </div>
      </div>

      <Card>
        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Tags</th>
              <th className="text-end">Reference files</th>
              <th>Changed</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="agents-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading the library
                </td>
              </tr>
            )}
            {!loading && agents.length === 0 && (
              <tr>
                <td colSpan={5} className="agents-empty">
                  No agents in the library yet. Upload one exported from Numa, or start a blank one.
                </td>
              </tr>
            )}
            {!loading &&
              agents.map((a) => (
                <tr key={a.library_agent_id}>
                  <td className="agents-table__what">
                    <div className="agents-table__title">{a.title}</div>
                    {a.description && <div className="agents-table__desc">{a.description}</div>}
                  </td>
                  <td>
                    <div className="agents-chips">
                      {(a.tags ?? []).slice(0, 4).map((t) => (
                        <span key={t} className="agents-chip">
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-end numeric">{a.reference_files?.length ?? 0}</td>
                  <td>
                    {a.updated_at && (
                      <>
                        <div title={`${formatNzDateTime(a.updated_at)} New Zealand time`}>{timeAgo(a.updated_at)}</div>
                        {a.updated_by && <div className="agents-sub">{a.updated_by}</div>}
                      </>
                    )}
                  </td>
                  <td className="agents-actions">
                    <Button variant="outline-secondary" size="sm" onClick={() => setEditing(a)}>
                      Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleting(a)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Card>

      {editing && (
        <AgentEditorModal
          draft={editing}
          warnings={importWarnings}
          onClose={() => {
            setEditing(null);
            setImportWarnings([]);
          }}
          onSaved={async () => {
            setEditing(null);
            setImportWarnings([]);
            await onChanged();
          }}
        />
      )}

      <Modal show={!!deleting} onHide={() => setDeleting(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{deleting?.title}</strong> leaves the library. A client that already has it keeps it.
          </p>
          <p className="mb-0">
            To take it off a client, send that client a set without it from the Send to clients tab.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleting(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={busy}>
            {busy && <Spinner size="sm" animation="border" className="me-1" />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ─── Editor modal ─────────────────────────────────────────────────────────────
function AgentEditorModal({
  draft,
  warnings = [],
  onClose,
  onSaved,
}: {
  draft: Partial<LibraryAgent>;
  warnings?: string[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<Partial<LibraryAgent>>(draft);
  const [tagsText, setTagsText] = useState((draft.tags ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // What went wrong in this dialog. It is shown here, where the person is looking.
  const [problem, setProblem] = useState<string | null>(null);

  const libraryAgentId = form.library_agent_id || draft.library_agent_id!;
  const tools = form.tools_config ?? {};

  // ENABLED integrations — what the agent actually has switched on. This is what
  // the deployed agent reflects: enabledIntegrations (unified, method-tagged) with
  // enabledConnections as the legacy fallback. NOTE: required_integrations is a
  // separate "must be connected" prerequisite, NOT an enabled tool — lumping it in
  // here was why Google Drive showed "on" in the portal but "off" once deployed.
  const integrationItems = (() => {
    const map = new Map<string, { slug: string; label: string; method?: string }>();
    for (const slug of tools.enabledConnections ?? []) {
      if (typeof slug === 'string') map.set(slug, { slug, label: slug });
    }
    for (const it of tools.enabledIntegrations ?? []) {
      map.set(it.slug, { slug: it.slug, label: it.name || it.slug, method: it.method });
    }
    return Array.from(map.values());
  })();

  // REQUIRED integrations — declared prerequisites the agent expects connected in
  // the target instance. Shown separately so they aren't mistaken for "enabled".
  const requiredItems = (form.required_integrations ?? []).filter((s): s is string => typeof s === 'string');

  const kbSummary = (() => {
    const akb = tools.allowedKnowledgeBases;
    if (akb === null || akb === undefined) return tools.queryDataSources === false ? 'None' : 'All';
    return akb.length === 0 ? 'None' : `${akb.length} chosen`;
  })();

  const setTool = (key: keyof NonNullable<LibraryAgent['tools_config']>, value: boolean) =>
    setForm((f) => ({ ...f, tools_config: { ...(f.tools_config ?? {}), [key]: value } }));

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setProblem(null);
    try {
      const uploaded: LibraryReferenceFile[] = [];
      for (const file of Array.from(files)) {
        uploaded.push(await arcanumAgentsLibraryService.uploadReferenceFile(libraryAgentId, file));
      }
      setForm((f) => ({ ...f, reference_files: [...(f.reference_files ?? []), ...uploaded] }));
    } catch (e) {
      setProblem(`The upload did not finish. ${reasonOf(e)} Upload the files again.`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (file: LibraryReferenceFile) => {
    setProblem(null);
    try {
      await arcanumAgentsLibraryService.deleteReferenceFile(libraryAgentId, file);
      setForm((f) => ({ ...f, reference_files: (f.reference_files ?? []).filter((x) => x.s3Key !== file.s3Key) }));
    } catch (e) {
      setProblem(`${file.fileName} was not removed. ${reasonOf(e)}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setProblem(null);
    try {
      const tags = tagsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      await arcanumAgentsLibraryService.saveAgent({ ...form, library_agent_id: libraryAgentId, tags });
      await onSaved();
    } catch (e) {
      setProblem(`The agent was not saved. ${reasonOf(e)}`);
      setSaving(false);
    }
  };

  const missingRequired = !form.title?.trim() || !form.system_prompt?.trim();

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {draft.updated_at ? 'Edit agent' : draft.title ? 'Check the imported agent' : 'New agent'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {warnings.length > 0 && (
          <Alert variant="warning">
            {warnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </Alert>
        )}
        {problem && (
          <Alert variant="danger" dismissible onClose={() => setProblem(null)}>
            {problem}
          </Alert>
        )}
        <Row className="g-3">
          <Col md={8}>
            <Form.Group controlId="agent-title">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={form.title ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="agent-type">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.agent_type ?? 'task'}
                onChange={(e) => setForm((f) => ({ ...f, agent_type: e.target.value }))}
              >
                {AGENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="agent-description">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="agent-system-prompt">
              <Form.Label>System prompt</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                className="agents-prompt"
                value={form.system_prompt ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
              />
              <Form.Text>What the agent is told before every conversation.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="agent-welcome">
              <Form.Label>Welcome message (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.user_instructions ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, user_instructions: e.target.value }))}
              />
              <Form.Text>What a person reads when they open the agent.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group controlId="agent-tags">
              <Form.Label>Tags (optional)</Form.Label>
              <Form.Control value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
              <Form.Text>Put a comma between tags.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="agent-icon">
              <Form.Label>Icon (optional)</Form.Label>
              <Form.Control
                value={form.icon ?? ''}
                className="mono"
                placeholder="bi-robot"
                onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              />
              <Form.Text>A Bootstrap icon class.</Form.Text>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Label>Tools</Form.Label>
            <div className="agents-switches">
              <Form.Check
                type="switch"
                id="tool-auto"
                label="Auto-select tools"
                checked={!!tools.autoToolsEnabled}
                onChange={(e) => setTool('autoToolsEnabled', e.currentTarget.checked)}
              />
              <Form.Check
                type="switch"
                id="tool-kb"
                label="Numa Files"
                checked={!!tools.queryDataSources}
                onChange={(e) => setTool('queryDataSources', e.currentTarget.checked)}
              />
              <Form.Check
                type="switch"
                id="tool-web"
                label="Web search"
                checked={!!tools.webSearchEnabled}
                onChange={(e) => setTool('webSearchEnabled', e.currentTarget.checked)}
              />
              <Form.Check
                type="switch"
                id="tool-create"
                label="Create agents"
                checked={!!tools.createAgentEnabled}
                onChange={(e) => setTool('createAgentEnabled', e.currentTarget.checked)}
              />
              <Form.Check
                type="switch"
                id="tool-memories"
                label="Memories"
                checked={!!tools.memoriesEnabled}
                onChange={(e) => setTool('memoriesEnabled', e.currentTarget.checked)}
              />
              <Form.Check
                type="switch"
                id="tool-ops"
                label="Numa Ops"
                checked={!!tools.numaOpsEnabled}
                onChange={(e) => setTool('numaOpsEnabled', e.currentTarget.checked)}
              />
            </div>
          </Col>

          <Col md={12}>
            <Form.Label>Integrations switched on</Form.Label>
            {integrationItems.length === 0 ? (
              <div className="agents-quiet">
                {tools.autoToolsEnabled
                  ? 'None are named. With Auto-select tools on, the agent uses any integration the client has connected.'
                  : 'None.'}
              </div>
            ) : (
              <div className="agents-chips">
                {integrationItems.map((it) => (
                  <span key={it.slug} className="agents-chip">
                    {it.label}
                    {it.method ? ` · ${it.method}` : ''}
                  </span>
                ))}
              </div>
            )}
            <Form.Text>Read from the exported agent. This page cannot change them.</Form.Text>
          </Col>

          {requiredItems.length > 0 && (
            <Col md={12}>
              <Form.Label>Integrations the client must have connected</Form.Label>
              <div className="agents-chips">
                {requiredItems.map((slug) => (
                  <span key={slug} className="agents-chip">
                    {slug}
                  </span>
                ))}
              </div>
              <Form.Text>
                The agent expects these on the client. They are not switched on as tools, so they show as off on the
                sent agent unless they are also in the list above.
              </Form.Text>
            </Col>
          )}

          <Col md={12}>
            <Form.Label>Other settings from the export</Form.Label>
            <dl className="tool-facts">
              <dt>Numa Files folders</dt>
              <dd>{kbSummary}</dd>
              {tools.approvalMode && (
                <>
                  <dt>Approval for integrations</dt>
                  <dd>{tools.approvalMode}</dd>
                </>
              )}
              {tools.approvalModes &&
                Object.entries(tools.approvalModes)
                  .filter(([, m]) => m)
                  .map(([cat, m]) => (
                    <Fragment key={cat}>
                      <dt>Approval for {cat}</dt>
                      <dd>{m}</dd>
                    </Fragment>
                  ))}
              {typeof form.estimated_time_saved_minutes === 'number' && (
                <>
                  <dt>Time saved, estimate</dt>
                  <dd>{form.estimated_time_saved_minutes} minutes</dd>
                </>
              )}
            </dl>
          </Col>

          <Col md={12}>
            <div className="agents-files-head">
              <Form.Label className="mb-0">Reference files</Form.Label>
              <Button as="label" size="sm" variant="outline-secondary" disabled={uploading}>
                {uploading ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <Upload className="me-1" aria-hidden="true" />
                )}
                {uploading ? 'Uploading' : 'Upload files'}
                <input type="file" multiple hidden onChange={(e) => handleUpload(e.target.files)} />
              </Button>
            </div>
            {(form.reference_files ?? []).length === 0 ? (
              <div className="agents-quiet">No reference files.</div>
            ) : (
              <ul className="agents-files">
                {(form.reference_files ?? []).map((file) => (
                  <li key={file.s3Key}>
                    <span>{file.fileName}</span>
                    <Button variant="outline-danger" size="sm" onClick={() => removeFile(file)}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {missingRequired && <span className="agents-footer-note">A title and a system prompt are needed.</span>}
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || uploading || missingRequired}>
          {saving && <Spinner size="sm" animation="border" className="me-1" />}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// A labelled list of agent names, for what a send changes on one client.
function AgentNameList({
  label,
  variant,
  items,
}: {
  label: string;
  variant: 'plain' | 'success' | 'danger';
  items: string[];
}) {
  return (
    <div className="agents-names">
      <span className="agents-names__label">{label}</span>
      {items.length === 0 ? (
        <span className="agents-quiet">None</span>
      ) : (
        <span className="agents-chips">
          {items.map((t, i) =>
            variant === 'plain' ? (
              <span key={`${t}-${i}`} className="agents-chip">
                {t}
              </span>
            ) : (
              <Badge key={`${t}-${i}`} bg={variant}>
                {t}
              </Badge>
            )
          )}
        </span>
      )}
    </div>
  );
}

// ─── Deploy tab ───────────────────────────────────────────────────────────────
function DeployTab({ agents, onError }: { agents: LibraryAgent[]; onError: (msg: string) => void }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [includeAll, setIncludeAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previews, setPreviews] = useState<PreviewResult[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  // Live per-client deploy status, keyed by client name (mirrors Support Docs Manager).
  const [deployStatuses, setDeployStatuses] = useState<Record<string, { state: DeployState; message: string }>>({});
  const [expandedClient, setExpandedClient] = useState<string | null>(null);

  useEffect(() => {
    clientService
      .getAllClients()
      .then(setClients)
      .catch((e) => onError(`The client list did not load. ${reasonOf(e)} Reload the page to try again.`))
      .finally(() => setLoading(false));
  }, [onError]);

  const selection = useMemo(() => ({ includeAll, agentIds: Array.from(selectedIds) }), [includeAll, selectedIds]);

  // Clear stale preview/results whenever the inputs change.
  useEffect(() => {
    setPreviews([]);
    setDeployStatuses({});
    setExpandedClient(null);
  }, [selectedClientNames, includeAll, selectedIds]);

  const updateDeployStatus = (name: string, status: { state: DeployState; message: string }) =>
    setDeployStatuses((prev) => ({ ...prev, [name]: status }));

  const handleClientToggle = (name: string) =>
    setSelectedClientNames((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const handleSelectClients = (names: string[]) => setSelectedClientNames(names);

  const toggleId = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const loadFromClient = async (name: string) => {
    try {
      const t = await arcanumAgentsLibraryService.getDeployTarget(name);
      setIncludeAll(t.includeAll);
      setSelectedIds(new Set(t.agentIds));
    } catch (e) {
      onError(`The set for ${name} did not load. ${reasonOf(e)}`);
    }
  };

  const runPreview = async (): Promise<PreviewResult[]> => {
    const out: PreviewResult[] = [];
    for (const name of selectedClientNames) {
      try {
        out.push(await agentDeployService.preview(name, selection));
      } catch (e) {
        onError(`${name} could not be checked, so it is left out. ${reasonOf(e)}`);
      }
    }
    setPreviews(out);
    return out;
  };

  const handleViewChanges = async () => {
    if (selectedClientNames.length === 0) return;
    setPreviewing(true);
    setDeployStatuses({});
    try {
      await runPreview();
    } finally {
      setPreviewing(false);
    }
  };

  const handleDeployClick = async () => {
    if (selectedClientNames.length === 0) return;
    setPreviewing(true);
    setDeployStatuses({});
    try {
      const p = previews.length ? previews : await runPreview();
      if (p.length) setConfirmOpen(true);
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmDeploy = async () => {
    setConfirmOpen(false);
    setDeploying(true);
    // Seed all targets as "queued" so the user sees the full list immediately,
    // then flip each to running → done as the loop progresses (per-client; each
    // client is a single Lambda call, so there's no sub-client progress).
    setDeployStatuses(
      Object.fromEntries(selectedClientNames.map((n) => [n, { state: 'queued' as const, message: '' }]))
    );
    for (const name of selectedClientNames) {
      updateDeployStatus(name, { state: 'running', message: '' });
      try {
        await arcanumAgentsLibraryService.setDeployTarget(name, selection);
        const r = await agentDeployService.deployToClient(name, selection);
        const state = r.status === 'failed' ? 'failed' : r.status === 'partial' ? 'partial' : 'success';
        const parts = [`${r.upserted.length} added or updated, ${r.removed.length} removed.`];
        if (r.error) parts.push(reasonOf(r.error));
        if (r.enforcementPresent === false) parts.push('People there can edit them until the next Numa deploy.');
        updateDeployStatus(name, { state, message: parts.join(' ') });
      } catch (e) {
        updateDeployStatus(name, { state: 'failed', message: reasonOf(e) });
      }
    }
    setDeploying(false);
    setPreviews([]);
    clientService.clearCache();
    clientService
      .getAllClients()
      .then(setClients)
      .catch(() => undefined);
  };

  const totalRemove = previews.reduce((n, p) => n + p.toRemove.length, 0);
  const nonEnforcing = previews.filter((p) => !p.enforcementPresent).map((p) => p.clientName);
  const selectedCount = selectedClientNames.length;
  const agentCount = includeAll ? agents.length : selectedIds.size;

  const statusEntries = Object.entries(deployStatuses);
  const doneCount = statusEntries.filter(([, s]) => s.state !== 'queued' && s.state !== 'running').length;
  const failedCount = statusEntries.filter(([, s]) => s.state === 'failed').length;
  const overallPercent = statusEntries.length === 0 ? 0 : Math.round((doneCount / statusEntries.length) * 100);

  return (
    <>
      <div className="agents-send">
        <Card>
          <Card.Header>Clients</Card.Header>
          <Card.Body>
            {loading ? (
              <div className="agents-loading">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading clients
              </div>
            ) : (
              <GroupedClientSelector
                clients={clients}
                selectedClientNames={selectedClientNames}
                onClientToggle={handleClientToggle}
                onSelectClients={handleSelectClients}
                disabled={deploying}
              />
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header className="agents-send__head">
            <span>Agents each client ends up with</span>
            {selectedCount === 1 && (
              <Button variant="outline-secondary" size="sm" onClick={() => loadFromClient(selectedClientNames[0])}>
                Start from {selectedClientNames[0]}&apos;s set
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            <Form.Check
              type="switch"
              id="include-all"
              className="agents-send__all"
              label={`Every agent in the library (${agents.length})`}
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.currentTarget.checked)}
            />
            <div className="agents-pick">
              {agents.length === 0 ? (
                <div className="agents-pick__empty">The library has no agents yet.</div>
              ) : (
                agents.map((a) => (
                  <div className="agents-pick__item" key={a.library_agent_id}>
                    <Form.Check
                      type="checkbox"
                      id={`sel-${a.library_agent_id}`}
                      label={a.title}
                      disabled={includeAll}
                      checked={includeAll || selectedIds.has(a.library_agent_id)}
                      onChange={() => toggleId(a.library_agent_id)}
                    />
                  </div>
                ))
              )}
            </div>
            <div className="tool-effect agents-send__effect">
              Every chosen client gets this same set of {countOf(agentCount, 'agent')}. On each one, a library agent
              that is not in the set is removed, with the files copied for it. Agents people made themselves are not
              touched.
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="agents-send__go">
        <span className="agents-send__hint">
          {selectedCount === 0
            ? 'Pick at least one client.'
            : 'See what changes first. Nothing is sent until you confirm it.'}
        </span>
        <Button variant="outline-secondary" onClick={handleViewChanges} disabled={previewing || selectedCount === 0}>
          {previewing && <Spinner size="sm" animation="border" className="me-1" />}
          See what changes
        </Button>
        <Button variant="primary" onClick={handleDeployClick} disabled={previewing || deploying || selectedCount === 0}>
          <Send className="me-1" aria-hidden="true" />
          Send to {countOf(selectedCount, 'client')}
        </Button>
      </div>

      {statusEntries.length > 0 && (
        <>
          <h2 className="section-label">Result by client</h2>
          <p className="agents-result-note">
            {doneCount} of {countOf(statusEntries.length, 'client')} finished
            {failedCount > 0 ? `, ${failedCount} failed` : ''}
            {deploying ? '. Still sending.' : '.'}
          </p>
          <Card className="agents-result">
            <Card.Body>
              <ProgressBar now={overallPercent} aria-label={`${overallPercent}% of clients finished`} />
            </Card.Body>
            <Table hover className="mb-0 agents-result__table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>State</th>
                  <th>What happened</th>
                </tr>
              </thead>
              <tbody>
                {statusEntries.map(([name, s]) => (
                  <tr key={name}>
                    <td className="mono">{name}</td>
                    <td>
                      <Badge bg={DEPLOY_BADGE[s.state].bg}>{DEPLOY_BADGE[s.state].label}</Badge>
                    </td>
                    <td className={s.state === 'failed' ? 'agents-bad' : undefined}>{s.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {previews.length > 0 && statusEntries.length === 0 && (
        <>
          <h2 className="section-label">What changes</h2>
          {nonEnforcing.length > 0 && (
            <Alert variant="warning">
              {countOf(nonEnforcing.length, 'client')} here {nonEnforcing.length === 1 ? 'runs' : 'run'} an older
              version of Numa: <strong>{nonEnforcing.join(', ')}</strong>. The agents arrive, but people there can edit
              them until that client&apos;s next Numa deploy.
            </Alert>
          )}
          {totalRemove > 0 && (
            <Alert variant="warning">
              This removes <strong>{countOf(totalRemove, 'agent')}</strong> across these clients, because{' '}
              {totalRemove === 1 ? 'it is' : 'they are'} not in the set.
            </Alert>
          )}
          <Card className="agents-changes">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th aria-label="Show the agents" />
                  <th>Client</th>
                  <th className="text-end">Added</th>
                  <th className="text-end">Updated</th>
                  <th className="text-end">Removed</th>
                  <th>Read-only for the client</th>
                </tr>
              </thead>
              <tbody>
                {previews.map((p) => {
                  const open = expandedClient === p.clientName;
                  return (
                    <Fragment key={p.clientName}>
                      <tr className="agents-changes__row" onClick={() => setExpandedClient(open ? null : p.clientName)}>
                        <td className="agents-changes__toggle">
                          <button
                            type="button"
                            className="agents-changes__button"
                            aria-expanded={open}
                            aria-label={
                              open ? `Hide the agents for ${p.clientName}` : `Show the agents for ${p.clientName}`
                            }
                          >
                            {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                          </button>
                        </td>
                        <td className="mono">{p.clientName}</td>
                        <td className="text-end numeric">{p.toAdd.length}</td>
                        <td className="text-end numeric">{p.toUpdate.length}</td>
                        <td className={`text-end numeric${p.toRemove.length > 0 ? ' agents-bad' : ''}`}>
                          {p.toRemove.length}
                        </td>
                        <td>
                          {p.enforcementPresent ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="warning">After its next deploy</Badge>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} className="agents-changes__detail">
                            <AgentNameList label="Has now" variant="plain" items={p.current.map((a) => a.title)} />
                            <AgentNameList label="Added" variant="success" items={p.toAdd.map((a) => a.title)} />
                            <AgentNameList label="Updated" variant="plain" items={p.toUpdate.map((a) => a.title)} />
                            <AgentNameList label="Removed" variant="danger" items={p.toRemove.map((a) => a.title)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send agents to {countOf(previews.length, 'client')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Each of these clients ends up with the set of {countOf(agentCount, 'agent')}. An updated agent is changed in
            place and keeps its id.
          </p>
          <Table size="sm" className="agents-confirm">
            <thead>
              <tr>
                <th>Client</th>
                <th className="text-end">Added</th>
                <th className="text-end">Updated</th>
                <th className="text-end">Removed</th>
              </tr>
            </thead>
            <tbody>
              {previews.map((p) => (
                <tr key={p.clientName}>
                  <td className="mono">{p.clientName}</td>
                  <td className="text-end numeric">{p.toAdd.length}</td>
                  <td className="text-end numeric">{p.toUpdate.length}</td>
                  <td className={`text-end numeric${p.toRemove.length > 0 ? ' agents-bad' : ''}`}>
                    {p.toRemove.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {totalRemove > 0 && (
            <Alert variant="warning">
              This removes {countOf(totalRemove, 'agent')}. A removed agent is deleted from the client with the files
              copied for it.
            </Alert>
          )}
          {nonEnforcing.length > 0 && (
            <p className="agents-quiet mb-0">
              On {nonEnforcing.join(', ')} people can edit the agents until the client&apos;s next Numa deploy.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmOpen(false)} disabled={deploying}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDeploy} disabled={deploying}>
            {deploying && <Spinner size="sm" animation="border" className="me-1" />}
            Send to {countOf(previews.length, 'client')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
